// Copyright: IQGeo Limited 2010-2022
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import i18n from 'i18next';
import Fetch from 'i18next-fetch-backend';

/** Localisation behaviour
 * @name LocalisationMixin
 * @mixin
 */
myw.LocalisationMixin =
    /** @lends LocalisationMixin */
    {
        /**
         * Returns a localized message.
         * Self's class will be used as the message group.
         * @param  {string}     messageId       Message key
         * @param  {string}     [positional]    Values for positional parameters in message
         * @return {string}     Translated message
         */
        msg(messageId, positional) {
            return myw.msg(this, messageId, positional);
        },

        /**
         * Walks over DOM tree and translates messages.
         * Self's class will be used as the message group
         * @param  {jQueryElement}    selector    jQuery selector for DOM tree.
         */
        translate(selector) {
            return myw.translate(this, selector);
        }
    };
myw.MywClass.include(myw.LocalisationMixin);

//****  add static localisation methods to myw ****
/**
 * Translate a message
 * @param  {string} messageGroup Name of message group
 * @param  {string} messageId    ID of message
 * @return {string} Translated message
 */
myw.msg = (messageGroup, messageId, positional) =>
    myw.localisation.msg(messageGroup, messageId, positional);

/**
 * Walks over DOM tree and translates messages
 * @param  {jQueryElement} selector jQuery selector for DOM tree.
 */
myw.translate = (groupOrObject, selector) => myw.localisation.translate(groupOrObject, selector);

myw.translateElement = (groupOrObject, element) =>
    myw.localisation.translateElement(groupOrObject, element);

/**
 * Set MessageGroup for each of the classes registered in the namespace. See myw.Localisation class for details.
 */
myw.setMessageGroups = () => {
    _.chain(myw)
        .keys()
        .filter(n => n[0].toUpperCase() == n[0])
        .each(n => {
            const proto = myw[n].prototype;
            if (proto && !Object.prototype.hasOwnProperty.call(proto, 'messageGroup')) {
                proto.messageGroup = n;
            }
        });
};

myw.Localisation = myw.MywClass.extend('Localisation', {
    /**
     * @class  Encapsulates localisation facilities for myWorld client
     * @constructs
     */
    initialize() {
        this.i18n = i18n;

        this.language = this._getUrlParam('lang') || navigator.language;

        this._chacheBust = myw.buildVersion ? '?' + myw.buildVersion : '';

        //ENH: add a "installed languages" configuration so that we could prevent
        // missing files from being requested (ex: simplify 'en-GB' to 'en')

        this._initialized = new Promise(resolve => {
            this._initializedResolve = resolve;
        });

        //setup warning if init() is not called
        setTimeout(() => {
            if (!this._initCalled)
                console.log(
                    'Warning: Localisation has not been initialized. Call myw.localisation.init()'
                );
        }, 5000);

        /** Will resolve when initialized and all requested namespaces have been loaded
         * @type {promise} */
        this.ready = new Promise(resolve => {
            this._readyResolve = resolve;
        });

        //  Keeps track of all of the promises generated by this class, used in conjunction with this.ready
        this._readyQueue = [];
    },

    /**
     * @type {promise}
     */
    get initialized() {
        if (!this._initialized)
            throw new Error('Localisation has not been initialized. Call myw.localisation.init()');
        return this._initialized;
    },

    /**
     * Adds a promise to a queue and resolves the ready promise when they are all fulfilled
     * @param {Promise} promise The promise to add to the tracking queue
     */
    _addToReadyQueue(promise) {
        this._readyQueue.push(promise);
        //  Once the promise is resolved, remove it from the list. Once all promises in the list are resolved, resolve the ready promise
        promise.then(() => {
            this._readyQueue = _.without(this._readyQueue, promise);
            if (this._readyQueue.length == 0) {
                this._readyResolve();
            }
        });
    },

    /**
     * Initialise localisation
     * @param  {Array<string>}  namespaces
     * @param  {object}     [options]
     * @param  {string[]}   [options.languages=['en']]   List of languages localised in the system.
     * @param  {string}     [options.localeDirectory='locales']   Locale directory. Defaults to 'locales'
     * @return {Promise}
     * @private
     */
    init(namespaces = [], options = {}) {
        this._initCalled = true;
        //include system.settings file as we'll need it to set font-family
        namespaces = ['myw.system.settings'].concat(namespaces);
        const { languages = ['en'] } = options;
        const langFamily = this.language ? this.language.split('-')[0] : this.language;
        const isExpectedLang = languages.includes(this.language) || languages.includes(langFamily);
        this.languageAsPerSetting =
            isExpectedLang && (languages.includes(this.language) ? this.language : langFamily);
        //  Use the first language in the list of expected languages as the fallback
        const fallbackLng = languages[0];

        const { localeDirectory = (myw.baseUrl || '') + 'locales' } = options;
        const newPromise = i18n
            .use(Fetch)
            .init({
                interpolation: { prefix: '__', suffix: '__' },
                backend: {
                    fetch: window.fetch, //  This may look redundant, but we need this for when Cordova overrides window.fetch in native apps
                    loadPath: `${localeDirectory}/__lng__/__ns__.msg${this._chacheBust}`
                },
                ns: namespaces,
                defaultNS: namespaces[0],
                fallbackNS: namespaces,
                lng: this.languageAsPerSetting || fallbackLng, //  Use fallback here to force default language if not in expected list
                fallbackLng, //  Use fallback here as well in case expected language has no msg files
                debug: false,
                returnObjectTrees: true,
                getAsync: true, // Do not set to false;  leads loading timeout problems with libraries
                useCookie: false,
                ...options
            })
            .then(() => {
                // Set the font family for the application if its is provided message file
                // (this over-rides the font-family set in CSS)
                $('body').css('font-family', myw.msg('Font', 'font-family'));

                this._initializedResolve();
            });
        this._addToReadyQueue(newPromise);
        return newPromise;
    },

    /**
     * Load name spaces from specified locale directory
     * @param  {Array<string>} namespaces           List of Namespace names. For example ['myw.redline']
     * @param  {string} [localeDirectory]   Locale directory. Defaults to 'locales'
     * @return {Promise}
     */
    loadNamespaces(namespaces, localeDirectory) {
        localeDirectory = localeDirectory || 'locales';

        const promise = new Promise((resolve, reject) => {
            const newPath = localeDirectory + '/__lng__/__ns__.msg' + this._chacheBust;

            //ensure we've initialized before proceeding
            this.initialized.then(() => {
                this.i18n.services.backendConnector.backend.options.loadPath = newPath;
                this.i18n.loadNamespaces(namespaces, resolve);
            }, reject);
        });

        this._addToReadyQueue(promise);
        return promise;
    },

    /**
     * Load name space from specified locale directory
     * @param  {string} namespace           Namespace name. For example 'myw.redline'
     * @param  {string} [localeDirectory]   Locale directory. Defaults to 'locales'
     * @return {Promise}
     */
    loadNamespace(namespace, localeDirectory) {
        return this.loadNamespaces([namespace], localeDirectory);
    },

    /**
     * Load name space for a module
     * @param  {string} moduleName Name of module
     * @param  {'client'|'config'} [application] Identifies if should load client or configuration file. Defaults to 'client'
     * @return {Promise}
     */
    loadModuleLocale(moduleName, application = 'client') {
        const appSuff = application == 'client' ? '' : '.' + application;
        return this.loadNamespace(moduleName + appSuff, `modules/${moduleName}/locales`);
    },

    /**
     * Walks over DOM tree and translates messages
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param  {jQuerySelector} selector jQuery selector for DOM tree.
     * @return {promise}  Resolves when translation has finished
     */
    translate(groupOrObject, selector) {
        return this.ready.then(() => {
            myw.trace('i18n', 3, 'Start DOM translate ' + selector.selector);
            this._translate(groupOrObject, selector);
            myw.trace('i18n', 3, 'End DOM translate');
        });
    },

    /**
     * Translate the inner HTML of an element
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param  {element} Element
     * @return {promise}  Resolves when translation has finished
     */
    translateElement(groupOrObject, element) {
        return this.ready.then(() => {
            myw.trace('i18n', 3, 'Start element translate ');
            element.text = this.translateHtml(groupOrObject, element.text);
            myw.trace('i18n', 3, 'End element translate');
        });
    },

    /**
     * Translate HTML string
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param {string} string to translate
     */
    translateHtml(groupOrObject, string) {
        const list = string.match(/{:(.*?)}/g);
        let i;
        let key;
        let value;

        if (!list) return string;

        for (i = 0; i < list.length; i++) {
            key = list[i].substring(2, list[i].length - 1);
            value = this.msg(groupOrObject, key);
            string = string.replace(list[i], value);
        }
        return string;
    },

    _translate(groupOrObject, selector) {
        let n, i, k;

        // Find text nodes with tag prefix and translate them)
        let nodes = selector
            .find('*')
            .contents()
            .filter(function () {
                //'this' will be jquery element - don't convert to arrow function
                return (
                    this.nodeType == 3 && this.data && this.data[0] && this.data[0].indexOf('{:')
                );
            });

        for (i = 0; i < nodes.length; i++) {
            n = nodes[i];
            n.data = this.translateHtml(groupOrObject, n.data);
        }

        // Find these attributes and translate them
        // ENH ID is needed for print layouts. Maybe make it optional
        const attrNames = ['title', 'alt', 'id', 'label'];

        for (k = 0; k < attrNames.length; k++) {
            const attrName = attrNames[k];
            nodes = selector.find('[' + attrName + "*='{:']");

            for (i = 0; i < nodes.length; i++) {
                n = $(nodes[i]);
                const attrValue = n.attr(attrName);

                // ENH Some cases the attr value is undefined even though it was picked up
                // in the find
                if (attrValue) n.attr(attrName, this.translateHtml(groupOrObject, attrValue));
            }
        }
    },

    /**
     * Translation function.
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param  {string} messageId Message key
     * @param  {string} positional Values for positional parameters in message string
     * @return {string} Translated message
     */
    msg(groupOrObject, messageId, positional) {
        let groupId;
        let proto;
        if (typeof groupOrObject == 'string') {
            // Replace / with . This sorts of groupOrObject strings can arising from config application where we
            // use the 'type' value which can sometimes be hierarchical (such as replicas/extract)
            groupId = groupOrObject.replace('/', '_');
        } else {
            proto = groupOrObject.constructor.prototype;
            groupId = proto.messageGroup;
        }

        if (!groupId) return messageId;

        let msg = this._msgInHierarchy(groupId, proto, messageId, positional);

        if (msg === undefined) msg = groupId + '.' + messageId; // return id showing missing message

        return msg;
    },

    // looks up message in hierarchy
    // tries both following myw_inherit_from message and given prototype (optional)
    _msgInHierarchy(groupId, proto, messageId, positional) {
        let msg = this._msg(groupId, messageId, positional);
        if (!isTranslated(msg)) {
            //try going up group in myw_inherit_from
            const inhGroupId = this._msg(groupId, 'myw_inherit_from');
            if (inhGroupId)
                msg = this._msgInHierarchy(inhGroupId, undefined, messageId, positional);

            if (!isTranslated(msg) && proto) {
                //try going up prototype chain
                proto = proto.constructor.__super__;
                const superGroupId = proto && proto.messageGroup;
                msg = this._msgInHierarchy(superGroupId, proto, messageId, positional);
            }
        }
        return msg;
    },

    // Looks up message
    _msg(prefix, messageId, positional) {
        let key,
            s,
            kprefix = '';

        if (prefix) {
            kprefix = prefix + '.';
        }

        const options = { returnObjects: true };
        if (positional) {
            _.extend(options, positional);
        }

        key = kprefix + messageId;

        s = this.i18n.t(key, options);
        if (s === key) return undefined;
        return s;
    },

    _getUrlParam(name) {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');

        const regexS = '[\\?&]' + name + '=([^&#]*)';
        const regex = new RegExp(regexS);
        const results = regex.exec(window.location.href);

        if (results === null) {
            return '';
        } else {
            return results[1];
        }
    },

    // Utility for finding all text nodes in the DOM that are not composed entirely of whitespace.
    findAll() {
        let i;
        let n;
        const result = document.evaluate(
            './/text()',
            document,
            null,
            XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
            null
        );

        for (i = 0; i < result.snapshotLength; i++) {
            n = result.snapshotItem(i);
            if (n.data.length > 0 && !n.data.match(/\s+/)) {
                // Go up looking for ancestor with an id. Helps to locate text in application
                let parent = n.parentElement,
                    id = '';
                while (parent) {
                    if (parent.id) {
                        id = parent.id;
                        break;
                    }
                    parent = parent.parentElement;
                }
                console.log(id, n.data);
            }
        }
    }
});

function isTranslated(msg) {
    return !(msg === undefined || (msg.startsWith && msg.startsWith('plural_not_found')));
}

// Return an instance of this class
myw.localisation = new myw.Localisation();
export default myw.localisation;
